import React, { useState, useEffect, useCallback } from 'react';
import { Page } from './types';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './pages/LandingPage';
import WorkspacePage from './pages/WorkspacePage';
import AboutPage from './pages/AboutPage';
import TemplatesPage from './pages/TemplatesPage';
import PricingPage from './pages/PricingPage';
import AIAgentsPage from './pages/AIAgentsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import Notification from './components/Notification';
import ScrollToTopButton from './components/ScrollToTopButton';
// FIX: Import the DashboardPage component to include it in the application's routing.
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import MobileDeploymentPage from './pages/MobileDeploymentPage';
import { useAuth } from './components/context/AuthContext';

export type NotificationType = 'success' | 'error' | 'info';

const pageMap: Record<Page, React.FC<any>> = {
    home: LandingPage,
    workspace: WorkspacePage,
    about: AboutPage,
    templates: TemplatesPage,
    pricing: PricingPage,
    ai_agents: AIAgentsPage,
    terms: TermsPage,
    privacy: PrivacyPolicyPage,
    faq: FAQPage,
    contact: ContactPage,
    // FIX: Add DashboardPage to the pageMap to make it a valid navigation target.
    dashboard: DashboardPage,
    login: LoginPage,
    signup: SignupPage,
    mobile_deployment: MobileDeploymentPage,
};

// Helper function to get page from path
const getPageFromPath = (): Page => {
    const path = window.location.pathname.substring(1);
    // Convert kebab-case URL to snake_case for Page type
    const pageKey = path.replace(/-/g, '_');

    if (pageKey && pageKey in pageMap) {
        return pageKey as Page;
    }
    return 'home';
};

const App: React.FC = () => {
    const { isAuthenticated, profile, isLoading: authLoading, login } = useAuth();
    const [theme, setTheme] = useState('dark');
    const [currentPage, setCurrentPage] = useState<Page>(getPageFromPath());
    const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ message: string; type: NotificationType } | null>(null);
    const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);

    useEffect(() => {
        document.documentElement.className = theme === 'dark' ? '' : 'light-mode';
    }, [theme]);

    // Effect to handle browser back/forward buttons
    useEffect(() => {
        const handleLocationChange = () => {
            setCurrentPage(getPageFromPath());
        };

        window.addEventListener('popstate', handleLocationChange);

        return () => {
            window.removeEventListener('popstate', handleLocationChange);
        };
    }, []);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'dark' ? 'light' : 'dark'));
    };

    const showNotification = (message: string, type: NotificationType) => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    const navigateTo = useCallback((page: Page, anchor?: string) => {
        // Update state directly for an immediate re-render
        setCurrentPage(page);

        // Convert snake_case Page type to kebab-case URL
        const path = page === 'home' ? '/' : `/${page.replace(/_/g, '-')}`;
        
        // Update URL to keep it in sync.
        if (window.location.pathname !== path) {
             window.history.pushState({ page }, '', path);
        }

        window.scrollTo(0, 0);

        if (anchor) {
            setTimeout(() => {
                const element = document.getElementById(anchor);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    }, []);

    // Effect to handle pending prompt after user logs in or selects plan
    useEffect(() => {
        if (pendingPrompt && isAuthenticated && !authLoading && profile) {
            const userPlan = profile?.plan?.toLowerCase();

            // If user now has a paid plan, redirect to workspace with prompt
            if (userPlan && userPlan !== 'starter') {
                setInitialPrompt(pendingPrompt);
                setPendingPrompt(null);
                navigateTo('workspace');
            }
        }
    }, [pendingPrompt, isAuthenticated, authLoading, profile, navigateTo]);

    const startWorkspaceWithPrompt = useCallback((prompt: string) => {
        // Store the prompt temporarily
        setPendingPrompt(prompt);

        // If user is not authenticated, redirect to login
        if (!isAuthenticated) {
            showNotification('Please log in to continue', 'info');
            login();
            return;
        }

        // If user is authenticated but has no profile yet (still loading), wait
        if (authLoading) {
            showNotification('Loading your profile...', 'info');
            return;
        }

        // Check if user has a valid plan or is a new user
        const userPlan = profile?.plan?.toLowerCase();
        const isNewUser = !profile || !userPlan || userPlan === 'starter';

        // If new user or starter plan, redirect to pricing page first
        if (isNewUser) {
            showNotification('Please select a plan to start creating', 'info');
            setInitialPrompt(prompt);
            navigateTo('pricing');
            return;
        }

        // User has valid plan, proceed to workspace
        setInitialPrompt(prompt);
        navigateTo('workspace');
    }, [isAuthenticated, authLoading, profile, login, navigateTo]);
    
    const clearInitialPrompt = () => {
        setInitialPrompt(null);
    };

    // Since getPageFromPath always returns a valid page (defaulting to 'home'),
    // CurrentPageComponent will always be a valid component. No fallback needed.
    const CurrentPageComponent = pageMap[currentPage];

    const pagesWithCompactFooter: Page[] = ['workspace', 'dashboard'];
    const isFooterCompact = pagesWithCompactFooter.includes(currentPage);
    
    const showHeaderFooter = !['login', 'signup'].includes(currentPage);

    return (
        <div className={`flex flex-col min-h-screen ${currentPage !== 'home' ? 'gradient-background' : ''}`}>
            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
            {showHeaderFooter && <Header theme={theme} toggleTheme={toggleTheme} navigateTo={navigateTo} />}
            <main className={`flex-grow flex flex-col ${showHeaderFooter ? 'pt-20' : ''}`}>
                <CurrentPageComponent 
                    theme={theme} 
                    navigateTo={navigateTo}
                    startWorkspaceWithPrompt={startWorkspaceWithPrompt}
                    initialPrompt={initialPrompt}
                    clearInitialPrompt={clearInitialPrompt}
                    showNotification={showNotification}
                    startOnboarding={startWorkspaceWithPrompt}
                />
            </main>
            {showHeaderFooter && <Footer navigateTo={navigateTo} isCompact={isFooterCompact} />}
            <ScrollToTopButton />
        </div>
    );
};

export default App;